/**
 * ClustersGraph — story bubble map (deepcontext-rlvt, restoring bd
 * deepcontext-b3to.8's pre-cutover force layout + zoom/pan).
 *
 * Draws the top 20 stories, ranked by 24h ad requests, as circle bubbles — area
 * proportional to `bid_requests_24h`, no minimum-readable-size floor — laid
 * out by a d3-force simulation. The real container size is measured
 * synchronously before the first paint (bd deepcontext-qaik), and the
 * simulation is run to convergence (`sim.stop()`, then `sim.tick()` the
 * number of steps `alphaDecay`/`alphaMin` would take) and collision-relaxed
 * before that first frame ever commits — no live `on('tick')` re-render loop,
 * no jump from an overlapped seed to the settled layout. A `forceRadial`
 * per-story target keeps rank legible: the highest-ranked story seeds at the
 * frame's centre and each lower rank's target radius grows outward, so
 * prominence reads as distance from centre rather than which nodes happen to
 * land next to which; a later real container resize re-settles the same way.
 *
 * Zoom/pan is a `d3-zoom` behaviour on a wrapping `<g>` transform, filtered
 * to the raw SVG background (wheel always zooms; drag only pans when it
 * starts on empty canvas, never on a bubble) — bubbles carry no drag handler
 * at all, so their position keeps encoding rank. A reset control returns the
 * view to the identity transform. Labels sit outside the zoomed group and are
 * re-fitted to each bubble's on-screen radius on every zoom change (semantic
 * zoom, bd deepcontext-ec6z.2), so zooming in reveals more of each headline
 * instead of just magnifying it.
 *
 * The remainder (`rest`) renders on the same canvas, not a ranked table —
 * restricted to stories still `live` (status 'story'; frozen stories are not
 * drawn), capped at `TAIL_DOT_CEILING`. Tail stories share the top 20's size
 * scale and a paler, desaturated fill. Once the simulation settles, the
 * `TAIL_LAID_OUT` largest relax in around the fixed top 20 and the rest are
 * specks scattered outside that area; relaxation runs until no two circles
 * touch. Position comes from size rank, never the story id.
 *
 * Every story is coloured by its IAB category (bd deepcontext-ec6z.3) — one
 * stable colour per category, grey when unclassified. A bottom-left legend
 * lists the categories on the map with their total story counts; hovering a row fades
 * every other story the same way Anchor does, and wins over Anchor meanwhile.
 *
 * When the whole market's traffic signal is zero (snapshots cleared, a
 * retention gap, a new market) bubbles size by `member_count` instead, and
 * the map header says so — otherwise every bubble collapses to nothing.
 *
 * Clicking a bubble opens `TopicDetailPanel` as a floating card anchored
 * beside the clicked bubble (not a fixed side panel) — the map keeps the
 * full frame width.
 *
 * Two layout modes (deepcontext-4776.4), switched by a "By topic | By size"
 * toggle top-right: 'size' is the rank-outward layout described above;
 * 'topic' (the default) places each story near its stored UMAP map coords
 * (`map_x`/`map_y`) via `settleTopicLayout`, so related stories read as
 * neighbourhoods, with a faint label over each tight IAB category. Resolution:
 * explicit `layout` prop > localStorage `clusters.layout` > 'topic'. Topic
 * mode needs >=80% of drawn stories to carry coords; otherwise the map falls
 * back to 'size' and the topic option is disabled until the next refresh.
 */

import React, {useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {forceCollide, forceManyBody, forceRadial, forceSimulation} from 'd3-force';
import {select} from 'd3-selection';
import {zoom, zoomIdentity, ZoomTransform} from 'd3-zoom';
import {useChartPalette} from '../../components/charts/useChartMode';
import {BubbleDefs, BubbleSelectionRing, bubbleShadow} from '../../components/charts/bubbleStyle';
import {iabCategoryColor} from '../../components/charts/chartTheme';
import {withAlpha} from '../../components/charts/options';
import {getMapPanelStyle} from '../../components/charts/mapPanelStyle';
import TopicDetailPanel from './TopicDetailPanel';
import {categoryLabels, hasMapCoords, settleTopicLayout, topicTargets} from './utils/topicLayout';
import type {ClustersTopicDetail, TrendingStoryPayload} from '../../api/clusters';

/** Top-20-by-24h-ad-requests cap (spec section 10). Enforced here too, defensively. */
export const CLUSTERS_TOP_N = 20;

/**
 * Safety net for a market whose live tail is still huge after the
 * still-live filter — e.g. TR's visible set was 819 stories with only 173
 * still live. Never draw more dots than this even so.
 */
export const TAIL_DOT_CEILING = 500;

/** No size floor (spec section 10): area, and therefore radius, scales purely with sqrt(bid_requests_24h). */
const MAX_R = 90;
/** Topic mode spreads stories across the whole frame, so bubbles cap smaller. */
const MAX_R_TOPIC = 60;
/** Not a visual floor — a few px of hit-target so a genuinely tiny story stays clickable. */
const MIN_INTERACTIVE_R = 3;
/** Margin kept clear between the outermost rank ring and the frame edge. */
const RANK_RING_MARGIN = 80;
/** Golden angle (radians) — spreads rank rings around the centre without radial clumping. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
/** Zoom range; the ceiling leaves room to read a tail story's label at full size. */
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 6;

/**
 * Top-20 force layout: run to convergence synchronously (d3's own documented
 * "skip the animation" pattern — `sim.stop()` then call `sim.tick()` the
 * number of times it would take alpha to decay below `alphaMin`) so the
 * settled layout is what commits on the very first paint, never an
 * overlapped seed that then animates apart.
 */
const SIM_ALPHA_DECAY = 0.03;
const SIM_ALPHA_MIN = 0.001;
const SIM_SETTLE_TICKS = Math.ceil(Math.log(SIM_ALPHA_MIN) / Math.log(1 - SIM_ALPHA_DECAY));

/**
 * The largest tail stories that join the collision layout around the fixed
 * top 20; the rest of the tail are specks placed outside that area.
 */
export const TAIL_LAID_OUT = 50;
/** Clear space kept between circles, per layer. */
const TOP_GAP = 6;
const LAID_GAP = 4;
const SPECK_GAP = 3;
/** Pull of the laid-out tail toward the centre, fading to zero over this many passes. */
const LAID_PULL = 0.01;
const LAID_PULL_PASSES = 300;
/** Safety stop for a relaxation that never settles; normal layouts stop as soon as nothing touches. */
const RELAX_MAX_EXTRA_PASSES = 3000;
const RELAX_TOLERANCE = 0.01;
/** Laid-out tail seeds start this far outside the top-20 extent, spiralling outward per rank. */
const LAID_SEED_OFFSET = 30;
const LAID_SEED_STEP = 4;
const LAID_SEED_ASPECT_Y = 0.8;
/** Specks scatter across a band this deep, starting this far outside the laid-out area. */
const SPECK_BAND_OFFSET = 20;
const SPECK_BAND_DEPTH = 260;
/** Specks spread wider than tall, to fill a landscape frame. */
const SPECK_ASPECT_X = 1.4;

/** Tail stories are paler: the palette colour mixed this far toward a neutral gray. */
const TAIL_DESATURATE_TOWARD = '#6b6b78';
const TAIL_DESATURATE_AMOUNT = 0.55;
/** On a light canvas the gray mix alone reads darker, not paler — lift it toward white too. */
const TAIL_LIGHT_MODE_LIFT = 0.35;
/** Same top-light sheen as the shared bubble gradient (bubbleStyle's TOP_SHEEN_AMOUNT). */
const TAIL_SHEEN_AMOUNT = 0.22;
/** Anchor / legend-hover fade for non-matching stories. */
const DIM_OPACITY = 0.22;
/** Legend key for stories with no IAB category. */
const NO_CATEGORY_KEY = 'none';
const NO_CATEGORY_LABEL = 'Uncategorised';
/** Legend overlay: translucent surface, capped at 5½ rows so the half row hints the list scrolls. */
const LEGEND_BG_ALPHA = 0.92;
const LEGEND_ROW_HEIGHT = 22;
const LEGEND_ROW_GAP = 6;
const LEGEND_PAD_Y = 10;
const LEGEND_VISIBLE_ROWS = 5.5;
const LEGEND_MAX_HEIGHT =
    LEGEND_PAD_Y + LEGEND_VISIBLE_ROWS * LEGEND_ROW_HEIGHT + Math.floor(LEGEND_VISIBLE_ROWS) * LEGEND_ROW_GAP;

const FONT_STACK = '"Plus Jakarta Sans", Inter, sans-serif';

export type ClustersLayout = 'topic' | 'size';
const LAYOUT_STORAGE_KEY = 'clusters.layout';
/** Share of drawn stories that must carry map coords before topic mode is offered. */
const TOPIC_COVERAGE_MIN = 0.8;
const TOPIC_UNAVAILABLE_HINT = 'Topic layout builds on the next hourly refresh';
/** Category labels are an overview aid — past this zoom the story labels take over. */
const CATEGORY_LABEL_MAX_ZOOM = 2;
const CATEGORY_LABEL_FONT_SIZE = 12;
const CATEGORY_LABEL_OPACITY = 0.55;

function readStoredLayout(): ClustersLayout | null {
    try {
        const v = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
        return v === 'topic' || v === 'size' ? v : null;
    } catch {
        return null;
    }
}

function storeLayout(layout: ClustersLayout): void {
    try {
        window.localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
    } catch {
        // Storage unavailable (private mode, quota) — the choice just won't persist.
    }
}

export function radiusFor(value: number, maxValue: number, maxR: number = MAX_R): number {
    const t = Math.sqrt(Math.max(value, 0)) / Math.sqrt(Math.max(maxValue, 1));
    return Math.max(MIN_INTERACTIVE_R, maxR * t);
}

interface Disc {
    x: number;
    y: number;
    r: number;
}

interface SimNode extends Disc {
    id: string;
    data: TrendingStoryPayload;
    vx?: number;
    vy?: number;
    /** IAB category key — picks the fill and the legend row. */
    catKey: string;
}

interface TailDot extends Disc {
    id: string;
    data: TrendingStoryPayload;
    catKey: string;
}

interface ClustersGraphProps {
    /** Top stories, ranked by 24h ad requests — sliced to CLUSTERS_TOP_N defensively. */
    top: TrendingStoryPayload[];
    /** Ranked remainder, same ordering — the live ones render as tail dots. */
    rest: TrendingStoryPayload[];
    /** map of String(story.id) -> fit score in [0,1]. Required when anchorActive. */
    fitMap?: Map<string, number>;
    anchorActive?: boolean;
    /** Highlight cutoff as a fraction of the best fit in `fitMap` — see the former genre map's identical knob. */
    threshold?: number;
    /** Fetch full story detail for the clicked bubble. Injected so this component stays fetch-free and testable. */
    loadDetail: (storyId: string) => Promise<ClustersTopicDetail>;
    /** Force a layout mode; otherwise the stored choice, else 'topic'. */
    layout?: ClustersLayout;
}

/** Deterministic pseudo-random in [0,1) from an index and a salt — same layout on every render. */
function seededRandom(i: number, salt: number): number {
    const v = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
    return v - Math.floor(v);
}

function mixHex(a: string, b: string, t: number): string {
    const parseHex = (hex: string): [number, number, number] => {
        const clean = hex.replace('#', '');
        const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
        const int = parseInt(full, 16);
        return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
    };
    const [ra, ga, ba] = parseHex(a);
    const [rb, gb, bb] = parseHex(b);
    const channel = (va: number, vb: number) => Math.round(va * (1 - t) + vb * t);
    return `#${[channel(ra, rb), channel(ga, gb), channel(ba, bb)]
        .map((v) => v.toString(16).padStart(2, '0'))
        .join('')}`;
}

function categoryKeyOf(story: TrendingStoryPayload): string {
    return story.iab_category ? String(story.iab_category.id) : NO_CATEGORY_KEY;
}

function categoryGradientId(idPrefix: string, catKey: string): string {
    return `${idPrefix}-cat-${catKey}`;
}

function tailGradientId(idPrefix: string, catKey: string): string {
    return `${idPrefix}-soft-${catKey}`;
}

interface LegendEntry {
    key: string;
    label: string;
    color: string;
    total: number;
}

/**
 * Collision relaxation: pushes `movable` circles apart from each other and
 * out of `fixed` ones, keeping `gap` px between edges. For the first
 * `pullPasses` passes each movable circle is also pulled toward (cx, cy),
 * with the pull fading to zero so it can't re-squash the layout; after that
 * it keeps separating until a whole pass finds no two circles touching.
 * Mutates `movable` in place.
 */
function relaxCircles(
    movable: Disc[],
    fixed: Disc[],
    gap: number,
    pullPasses: number,
    pull: number,
    cx: number,
    cy: number,
): void {
    for (let pass = 0; pass < pullPasses + RELAX_MAX_EXTRA_PASSES; pass += 1) {
        const g = pass < pullPasses ? pull * (1 - pass / pullPasses) : 0;
        if (g > 0) {
            for (const c of movable) {
                c.x -= (c.x - cx) * g;
                c.y -= (c.y - cy) * g;
            }
        }
        let worst = 0;
        for (let a = 0; a < movable.length; a += 1) {
            const A = movable[a];
            for (const B of fixed) {
                const dx = A.x - B.x;
                const dy = A.y - B.y;
                const d = Math.hypot(dx, dy) || 0.01;
                const min = A.r + B.r + gap;
                if (d < min) {
                    worst = Math.max(worst, min - d);
                    A.x += (dx / d) * (min - d);
                    A.y += (dy / d) * (min - d);
                }
            }
            for (let b = a + 1; b < movable.length; b += 1) {
                const B = movable[b];
                const dx = A.x - B.x;
                const dy = A.y - B.y;
                const d = Math.hypot(dx, dy) || 0.01;
                const min = A.r + B.r + gap;
                if (d < min) {
                    worst = Math.max(worst, min - d);
                    const push = (min - d) / 2;
                    A.x += (dx / d) * push;
                    A.y += (dy / d) * push;
                    B.x -= (dx / d) * push;
                    B.y -= (dy / d) * push;
                }
            }
        }
        if (pass >= pullPasses && worst <= RELAX_TOLERANCE) return;
    }
}

function extentFrom(cx: number, cy: number, discs: Disc[]): number {
    return discs.reduce((m, d) => Math.max(m, Math.hypot(d.x - cx, d.y - cy) + d.r), 0);
}

/**
 * Tail geometry (locked mockup's "Proposed" mode): the `TAIL_LAID_OUT`
 * largest tail circles seed on a spiral just outside the (fixed) top 20 and
 * relax in toward the centre until nothing touches; the rest are specks
 * scattered in a band outside that area and only nudged apart. Returns one
 * position per entry of `radii`, same order. Deterministic: position comes
 * from size rank, never the story id.
 */
function layoutTail(top: Disc[], radii: number[], cx: number, cy: number): Disc[] {
    const out: Disc[] = radii.map((r) => ({x: cx, y: cy, r}));
    const bySize = radii.map((_, i) => i).sort((a, b) => radii[b] - radii[a] || a - b);
    const laid = bySize.slice(0, TAIL_LAID_OUT).map((i) => out[i]);
    const specks = bySize.slice(TAIL_LAID_OUT).map((i) => out[i]);

    const topExtent = extentFrom(cx, cy, top);
    laid.forEach((c, j) => {
        const angle = j * GOLDEN_ANGLE + 0.7;
        const dist = topExtent + LAID_SEED_OFFSET + j * LAID_SEED_STEP;
        c.x = cx + Math.cos(angle) * dist;
        c.y = cy + Math.sin(angle) * dist * LAID_SEED_ASPECT_Y;
    });
    relaxCircles(laid, top, LAID_GAP, LAID_PULL_PASSES, LAID_PULL, cx, cy);

    const laidExtent = Math.max(topExtent, extentFrom(cx, cy, laid));
    specks.forEach((c, j) => {
        const angle = seededRandom(j, 9) * Math.PI * 2;
        const dist = laidExtent + SPECK_BAND_OFFSET + c.r + seededRandom(j, 10) * SPECK_BAND_DEPTH;
        c.x = cx + Math.cos(angle) * dist * SPECK_ASPECT_X;
        c.y = cy + Math.sin(angle) * dist;
    });
    relaxCircles(specks, [...top, ...laid], SPECK_GAP, 0, 0, cx, cy);
    return out;
}

function subLabelFor(story: TrendingStoryPayload): string {
    return `${story.member_count.toLocaleString()} pages · ${story.domain_count.toLocaleString()} domains · ${story.bid_requests_24h.toLocaleString()} ad req`;
}

function headlineFor(story: TrendingStoryPayload): string {
    return story.headline ?? story.event ?? `Story ${story.id}`;
}

/** Hover-tooltip geometry (deepcontext-ayu7, locked design). */
const TOOLTIP_OFFSET_X = 14;
const TOOLTIP_OFFSET_Y = -50;
const TOOLTIP_MAX_WIDTH = 260;
const TOOLTIP_FALLBACK_HEIGHT = 70;
const TOOLTIP_EDGE_PAD = 6;

interface TooltipState {
    x: number;
    y: number;
    story: TrendingStoryPayload;
}

/**
 * Cursor-anchored position for the hover tooltip — offset `+14x/-50y` from
 * the pointer, clamped inside the map frame, flipping to the left of the
 * cursor when it would overflow the right edge. Ported from the locked
 * design_html mockup's inline positioning script.
 */
function computeTooltipPosition(
    cursor: {x: number; y: number},
    frame: {w: number; h: number},
    tip: {w: number; h: number},
): {left: number; top: number} {
    let left = cursor.x + TOOLTIP_OFFSET_X;
    if (left + tip.w > frame.w - TOOLTIP_EDGE_PAD) {
        left = cursor.x - TOOLTIP_OFFSET_X - tip.w;
    }
    let top = cursor.y + TOOLTIP_OFFSET_Y;
    if (top < TOOLTIP_EDGE_PAD) top = TOOLTIP_EDGE_PAD;
    if (top + tip.h > frame.h - TOOLTIP_EDGE_PAD) top = frame.h - tip.h - TOOLTIP_EDGE_PAD;
    return {left, top};
}

function clamp(min: number, max: number, v: number): number {
    return Math.min(max, Math.max(min, v));
}

/**
 * Word-wrap `text` to fit `maxW` px at `font`, at most `maxLines` lines —
 * ported 1:1 from the approved prototype's `wrap()` (page.html). Overflow
 * truncates the last line with an ellipsis, shrinking char-by-char until it
 * fits — this is the only thing keeping a long headline inside its circle.
 */
function wrapLines(
    measure: (text: string) => number,
    text: string,
    maxW: number,
    maxLines: number,
): string[] {
    const words = String(text).split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let cur = '';
    for (const word of words) {
        const t = cur ? `${cur} ${word}` : word;
        if (!cur || measure(t) <= maxW) {
            cur = t;
        } else {
            lines.push(cur);
            cur = word;
        }
    }
    if (cur) lines.push(cur);
    if (lines.length > maxLines) {
        lines.length = maxLines;
        lines[maxLines - 1] += '…';
    }
    return lines.map((l) => {
        if (measure(l) <= maxW) return l;
        let s = l.replace(/…$/, '');
        while (s.length > 1 && measure(`${s}…`) > maxW) s = s.slice(0, -1).trimEnd();
        return `${s}…`;
    });
}

interface WrappedLabel {
    lines: string[];
    fontSize: number;
    lineHeight: number;
    /** Zero, one ("N pages · M domains · X ad req") or two stats lines. */
    subLines: string[];
    subFontSize: number;
    subLineHeight: number;
}

/**
 * In-circle label geometry for a bubble whose ON-SCREEN radius is `r`
 * (bubble radius × zoom) — the locked mockup's semantic-zoom rule. Font size
 * and line count grow with the on-screen size (10–17px, up to 8 headline
 * lines); a one-line stats sub-line shows from 30px, a two-line stats block
 * from 110px; and no label renders at all below 16px — those bubbles stay
 * reachable via the hover tooltip.
 */
export function computeLabel(
    measure: (text: string, font: string) => number,
    story: TrendingStoryPayload,
    r: number,
): WrappedLabel | null {
    if (r < 16) return null;
    const fontSize = clamp(10, 17, r / 5.2);
    const subFontSize = clamp(9, 12, fontSize - 3);
    const lineHeight = fontSize * 1.2;
    const subLineHeight = subFontSize * 1.3;
    const maxWidth = r * 1.6;
    const subCount = r >= 110 ? 2 : r >= 30 ? 1 : 0;
    const maxLines = clamp(1, 8, Math.floor((r * 1.4 - subCount * subLineHeight) / lineHeight));
    const headlineFont = `600 ${fontSize}px ${FONT_STACK}`;
    const lines = wrapLines((t) => measure(t, headlineFont), headlineFor(story), maxWidth, maxLines);
    const subFont = `400 ${subFontSize}px ${FONT_STACK}`;
    const fitSub = (text: string) => wrapLines((t) => measure(t, subFont), text, maxWidth, 1)[0];
    const subLines =
        subCount === 2
            ? [
                  `${story.member_count.toLocaleString()} pages · ${story.domain_count.toLocaleString()} domains`,
                  `${story.bid_requests_24h.toLocaleString()} ad requests (24h)`,
              ].map(fitSub)
            : subCount === 1
              ? [fitSub(subLabelFor(story))]
              : [];
    return {lines, fontSize, lineHeight, subLines, subFontSize, subLineHeight};
}

const ClustersGraph: React.FC<ClustersGraphProps> = ({
    top,
    rest,
    fitMap,
    anchorActive = false,
    threshold = 0.5,
    loadDetail,
    layout,
}) => {
    const topStories = useMemo(() => top.slice(0, CLUSTERS_TOP_N), [top]);
    /** No story in the market carries traffic — size by page count rather than collapse every bubble. */
    const trafficAbsent = topStories.length > 0 && topStories.every((s) => s.bid_requests_24h <= 0);

    const tailStories = useMemo(
        () => rest.filter((s) => s.status === 'story').slice(0, TAIL_DOT_CEILING),
        [rest],
    );

    const [chosenLayout, setChosenLayout] = useState<ClustersLayout>(() => layout ?? readStoredLayout() ?? 'topic');
    useEffect(() => {
        if (layout) setChosenLayout(layout);
    }, [layout]);
    const topicAvailable = useMemo(() => {
        const drawn = topStories.length + tailStories.length;
        if (drawn === 0) return false;
        const withCoords = topStories.filter(hasMapCoords).length + tailStories.filter(hasMapCoords).length;
        return withCoords / drawn >= TOPIC_COVERAGE_MIN;
    }, [topStories, tailStories]);
    const activeLayout: ClustersLayout = chosenLayout === 'topic' && topicAvailable ? 'topic' : 'size';
    const isTopic = activeLayout === 'topic';

    function handleLayoutChange(next: ClustersLayout) {
        setChosenLayout(next);
        storeLayout(next);
    }

    const fitCutoff = useMemo<number>(() => {
        if (!anchorActive || !fitMap || fitMap.size === 0) return 0;
        let maxFit = 0;
        fitMap.forEach((v) => {
            if (v > maxFit) maxFit = v;
        });
        return maxFit * threshold;
    }, [anchorActive, fitMap, threshold]);

    const palette = useChartPalette();
    const bubbleIdPrefix = useId();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const nodesRef = useRef<SimNode[]>([]);
    const zoomBehaviorRef = useRef<ReturnType<typeof zoom<Element, unknown>> | null>(null);
    // A single 2D canvas context reused for every `measureText` call — same
    // approach as the approved prototype's module-level `canvas` (page.html).
    const measureCtxRef = useRef<CanvasRenderingContext2D | null>(null);
    if (measureCtxRef.current === null && typeof document !== 'undefined') {
        measureCtxRef.current = document.createElement('canvas').getContext('2d');
    }

    const [size, setSize] = useState<{w: number; h: number}>({w: 1200, h: 640});
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [isPanning, setIsPanning] = useState(false);
    const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);
    /** Top-20 positions the tail is laid out around — refreshed when the simulation settles. */
    const [settled, setSettled] = useState<Disc[]>([]);
    const [tip, setTip] = useState<TooltipState | null>(null);
    /** Category key of the hovered legend row, if any. */
    const [hoverCat, setHoverCat] = useState<string | null>(null);
    // Mounting the tooltip div with `opacity: 1` already baked in gives the
    // browser no prior state to interpolate from, so its CSS transition never
    // plays. Track "entered" separately: a fresh show (null -> non-null)
    // mounts at opacity 0, then flips to 1 next frame so the 120ms fade-in
    // actually animates. Moving to a different bubble while already showing
    // doesn't reset it — the design only fades in on first reveal.
    const [tipEntered, setTipEntered] = useState(false);
    const tipRef = useRef<HTMLDivElement | null>(null);
    const tipEnterRafRef = useRef<number | null>(null);

    useEffect(
        () => () => {
            if (tipEnterRafRef.current != null) cancelAnimationFrame(tipEnterRafRef.current);
        },
        [],
    );

    const [detail, setDetail] = useState<ClustersTopicDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);

    useEffect(() => {
        if (!expandedId) {
            setDetail(null);
            setDetailError(null);
            setDetailLoading(false);
            return;
        }
        let cancelled = false;
        setDetailLoading(true);
        setDetailError(null);
        loadDetail(expandedId)
            .then((d) => {
                if (cancelled) return;
                setDetail(d);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : 'Failed to load story detail.';
                setDetailError(message);
            })
            .finally(() => {
                if (cancelled) return;
                setDetailLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedId]);

    /**
     * Rank-outward seed: index 0 (the highest-ranked story) starts at the
     * exact centre, and each subsequent rank's target distance from centre
     * grows linearly — spread around a golden-angle spiral so rings don't
     * clump on one side. This is also the layout the simulation converges
     * toward via the 'rank' force below; it's a seed, not what ever paints
     * (deepcontext-qaik settles to convergence before the first commit).
     */
    const initial = useMemo<SimNode[]>(() => {
        const maxBid = topStories.reduce((m, s) => Math.max(m, s.bid_requests_24h), 1);
        const maxMembers = topStories.reduce((m, s) => Math.max(m, s.member_count), 1);
        const cx = size.w / 2;
        const cy = size.h / 2;
        const maxDist = Math.max(0, Math.min(size.w, size.h) / 2 - RANK_RING_MARGIN);
        const n = topStories.length;
        return topStories.map((s, i) => {
            const dist = n <= 1 ? 0 : (i / (n - 1)) * maxDist;
            const angle = i * GOLDEN_ANGLE;
            return {
                id: String(s.id),
                data: s,
                r: trafficAbsent ? radiusFor(s.member_count, maxMembers) : radiusFor(s.bid_requests_24h, maxBid),
                x: cx + Math.cos(angle) * dist,
                y: cy + Math.sin(angle) * dist,
                catKey: categoryKeyOf(s),
            };
        });
    }, [topStories, trafficAbsent, size]);

    /** Tail stories on the same size scale as the top 20 — honest size, not a random dot. */
    const tailRadii = useMemo<number[]>(() => {
        const metric = (s: TrendingStoryPayload) => (trafficAbsent ? s.member_count : s.bid_requests_24h);
        const max = topStories.reduce((m, s) => Math.max(m, metric(s)), 1);
        return tailStories.map((s) => radiusFor(metric(s), max));
    }, [tailStories, topStories, trafficAbsent]);

    const tailLayout = useMemo<Disc[]>(
        () => (isTopic ? [] : layoutTail(settled, tailRadii, size.w / 2, size.h / 2)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [settled, tailRadii, isTopic],
    );

    /**
     * Topic mode: every drawn story targets its map coords; the top 20 plus
     * the `TAIL_LAID_OUT` largest tail stories settle as majors, the rest as
     * specks around them. Settled synchronously, so the first frame is final.
     */
    const topicLayout = useMemo<{top: SimNode[]; tail: Disc[]} | null>(() => {
        if (!isTopic) return null;
        const metric = (s: TrendingStoryPayload) => (trafficAbsent ? s.member_count : s.bid_requests_24h);
        const max = topStories.reduce((m, s) => Math.max(m, metric(s)), 1);
        const drawn = [...topStories, ...tailStories];
        const targets = topicTargets(
            drawn.map((s) => ({
                mapX: s.map_x,
                mapY: s.map_y,
                cat: s.iab_category ? String(s.iab_category.id) : null,
                weight: s.member_count,
            })),
            size.w,
            size.h,
        );
        const radii = drawn.map((s) => radiusFor(metric(s), max, MAX_R_TOPIC));
        const nTop = topStories.length;
        const majorTail = new Set(
            tailStories
                .map((_, i) => nTop + i)
                .sort((a, b) => radii[b] - radii[a] || a - b)
                .slice(0, TAIL_LAID_OUT),
        );
        const discs = settleTopicLayout(
            drawn.map((_, i) => ({r: radii[i], tx: targets[i].x, ty: targets[i].y, major: i < nTop || majorTail.has(i)})),
        );
        return {
            top: topStories.map((s, i) => ({id: String(s.id), data: s, catKey: categoryKeyOf(s), ...discs[i]})),
            tail: discs.slice(nTop),
        };
    }, [isTopic, topStories, tailStories, trafficAbsent, size]);

    /**
     * Measure the real container size before the browser paints (a
     * `useLayoutEffect` runs synchronously after DOM mutations but before
     * paint), so the very first frame the user sees lays out against the
     * actual frame, never the pre-mount placeholder in `size`'s initial
     * state. `ResizeObserver` still tracks later size changes (below).
     */
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            setSize((prev) => (prev.w === rect.width && prev.h === rect.height ? prev : {w: rect.width, h: rect.height}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) {
                const r = e.contentRect;
                if (r.width > 0 && r.height > 0) {
                    setSize((prev) => (prev.w === r.width && prev.h === r.height ? prev : {w: r.width, h: r.height}));
                }
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    /**
     * Build the top-20 force layout and settle it to convergence
     * synchronously — d3's own documented pattern for skipping the
     * animation (`sim.stop()`, then call `sim.tick()` the number of times
     * alpha would take to decay below `alphaMin`) — then finish separating
     * any bubbles forceCollide only approximately pushed apart. This commits
     * once, already settled: no `on('tick')` re-render loop, no async
     * `on('end')` jump. Depends on `size` (via `initial`) so a real
     * container resize re-settles the same way, instantly, instead of
     * animating a second time.
     */
    useEffect(() => {
        if (isTopic) return;
        const nodes = initial.map((d) => ({...d}));
        const n = nodes.length;
        const cx = size.w / 2;
        const cy = size.h / 2;
        const maxDist = Math.max(0, Math.min(size.w, size.h) / 2 - RANK_RING_MARGIN);
        const sim = forceSimulation<SimNode>(nodes)
            .force('charge', forceManyBody<SimNode>().strength(-140))
            .force('collide', forceCollide<SimNode>((d) => d.r + 4))
            .force(
                'rank',
                forceRadial<SimNode>((_d, i) => (n <= 1 ? 0 : (i / (n - 1)) * maxDist), cx, cy).strength(0.15),
            )
            .alpha(0.9)
            .alphaDecay(SIM_ALPHA_DECAY)
            .stop();
        for (let i = 0; i < SIM_SETTLE_TICKS; i += 1) sim.tick();
        // forceCollide only approximately separates bubbles; finish the job
        // so no two top-20 bubbles touch before this ever paints.
        relaxCircles(nodes, [], TOP_GAP, 0, 0, cx, cy);
        nodesRef.current = nodes;
        setSettled(nodes.map(({x, y, r}) => ({x, y, r})));
    }, [initial, size, isTopic]);

    /**
     * d3-zoom on a wrapping `<g>` transform — no per-node drag comes back
     * (bd deepcontext-rlvt: position keeps encoding rank). Filtered to the
     * raw `<svg>` background so a drag started on a bubble neither pans the
     * canvas nor is mistaken for one; wheel always zooms.
     */
    useEffect(() => {
        if (!svgRef.current) return undefined;
        const svg = select(svgRef.current as Element);
        const z = zoom<Element, unknown>()
            .scaleExtent([ZOOM_MIN, ZOOM_MAX])
            .filter((event: Event) => {
                const mouseEvent = event as MouseEvent & {shiftKey: boolean};
                if (event.type === 'wheel') return true;
                if (mouseEvent.button === 1 || mouseEvent.button === 2) return true;
                if (mouseEvent.shiftKey) return true;
                const target = event.target as Element | null;
                return target?.tagName === 'svg';
            })
            .on('start', () => setIsPanning(true))
            .on('zoom', (event: {transform: ZoomTransform}) => setTransform(event.transform))
            .on('end', () => setIsPanning(false));
        svg.call(z as never);
        zoomBehaviorRef.current = z;
        const ctxHandler = (e: Event) => e.preventDefault();
        svgRef.current.addEventListener('contextmenu', ctxHandler);
        const node = svgRef.current;
        return () => {
            svg.on('.zoom', null);
            node.removeEventListener('contextmenu', ctxHandler);
        };
    }, []);

    function handleResetView() {
        if (!svgRef.current || !zoomBehaviorRef.current) return;
        select(svgRef.current as Element).call(zoomBehaviorRef.current.transform as never, zoomIdentity as never);
    }

    function handleZoomBy(factor: number) {
        if (!svgRef.current || !zoomBehaviorRef.current) return;
        select(svgRef.current as Element).call(zoomBehaviorRef.current.scaleBy as never, factor as never);
    }

    function measure(text: string, font: string): number {
        const ctx = measureCtxRef.current;
        if (!ctx) return text.length * 6;
        ctx.font = font;
        return ctx.measureText(text).width;
    }

    function handleSelect(id: string) {
        setExpandedId((prev) => (prev === id ? null : id));
    }

    function handleBackgroundClick(e: React.MouseEvent<SVGSVGElement>) {
        if (e.target === svgRef.current) {
            setExpandedId(null);
        }
    }

    function handleBubbleMove(e: React.MouseEvent, story: TrendingStoryPayload) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setTip((prev) => {
            if (!prev) {
                setTipEntered(false);
                if (tipEnterRafRef.current != null) cancelAnimationFrame(tipEnterRafRef.current);
                tipEnterRafRef.current = requestAnimationFrame(() => setTipEntered(true));
            }
            return {x: e.clientX - rect.left, y: e.clientY - rect.top, story};
        });
    }

    function handleBubbleLeave() {
        setTip(null);
        setTipEntered(false);
        if (tipEnterRafRef.current != null) {
            cancelAnimationFrame(tipEnterRafRef.current);
            tipEnterRafRef.current = null;
        }
    }

    const simNodes = topicLayout ? topicLayout.top : nodesRef.current;
    const tailDots: TailDot[] = (topicLayout ? topicLayout.tail : tailLayout).map((p, i) => ({
        id: String(tailStories[i].id),
        data: tailStories[i],
        catKey: categoryKeyOf(tailStories[i]),
        ...p,
    }));

    /** Every category on the map, most stories first — one colour per category (the legend lists them all). */
    const categories = useMemo<LegendEntry[]>(() => {
        const byKey = new Map<string, LegendEntry>();
        const add = (s: TrendingStoryPayload) => {
            const key = categoryKeyOf(s);
            let entry = byKey.get(key);
            if (!entry) {
                entry = {
                    key,
                    label: s.iab_category?.label ?? NO_CATEGORY_LABEL,
                    color: iabCategoryColor(s.iab_category?.id ?? null, palette.mode),
                    total: 0,
                };
                byKey.set(key, entry);
            }
            entry.total += 1;
        };
        topStories.forEach(add);
        tailStories.forEach(add);
        return [...byKey.values()].sort(
            (a, b) =>
                Number(a.key === NO_CATEGORY_KEY) - Number(b.key === NO_CATEGORY_KEY) ||
                b.total - a.total ||
                a.label.localeCompare(b.label),
        );
    }, [topStories, tailStories, palette.mode]);

    /** Faint neighbourhood names over each tight category — topic mode only, overview zoom only. */
    const categoryMarks = useMemo(() => {
        if (!isTopic || transform.k > CATEGORY_LABEL_MAX_ZOOM) return [];
        return categoryLabels(
            [...simNodes, ...tailDots].map((c) => ({
                cat: c.data.iab_category ? c.catKey : null,
                label: c.data.iab_category?.label ?? NO_CATEGORY_LABEL,
                x: c.x,
                y: c.y,
                r: c.r,
                weight: c.data.member_count,
            })),
            size.w,
            size.h,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTopic, topicLayout, transform.k, size]);

    /** Legend hover wins over the Anchor fade while it lasts. */
    function dimFor(id: string, catKey: string): number {
        if (hoverCat != null) return catKey === hoverCat ? 1 : DIM_OPACITY;
        if (!anchorActive) return 1;
        const f = fitMap ? fitMap.get(id) ?? 0 : 0;
        return fitCutoff > 0 && f >= fitCutoff ? 1 : DIM_OPACITY;
    }

    const expandedNode = expandedId != null ? simNodes.find((n) => n.id === expandedId) ?? null : null;
    const cursorStyle = isPanning ? 'grabbing' : 'default';
    const panelStyle = getMapPanelStyle(palette.mode);
    const tooltipPos = tip
        ? computeTooltipPosition(
              {x: tip.x, y: tip.y},
              {w: size.w, h: size.h},
              {
                  w: tipRef.current?.offsetWidth || TOOLTIP_MAX_WIDTH,
                  h: tipRef.current?.offsetHeight || TOOLTIP_FALLBACK_HEIGHT,
              },
          )
        : null;

    return (
        <div
            ref={containerRef}
            data-testid="clusters-graph"
            data-layout={activeLayout}
            className="relative w-full h-full min-h-0 rounded-2xl overflow-hidden"
            style={{
                cursor: cursorStyle,
                minHeight: 420,
                ...panelStyle,
            }}
        >
            <svg
                ref={svgRef}
                data-testid="clusters-svg"
                width="100%"
                height="100%"
                viewBox={`0 0 ${size.w} ${size.h}`}
                preserveAspectRatio="xMidYMid meet"
                style={{position: 'absolute', inset: 0}}
                onClick={handleBackgroundClick}
            >
                <BubbleDefs idPrefix={bubbleIdPrefix} palette={palette} />
                <defs>
                    {categories.map(({key, color}) => {
                        const gray = mixHex(color, TAIL_DESATURATE_TOWARD, TAIL_DESATURATE_AMOUNT);
                        const soft = palette.mode === 'light' ? mixHex(gray, '#ffffff', TAIL_LIGHT_MODE_LIFT) : gray;
                        return (
                            <React.Fragment key={key}>
                                <linearGradient id={categoryGradientId(bubbleIdPrefix, key)} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={mixHex(color, '#ffffff', TAIL_SHEEN_AMOUNT)} />
                                    <stop offset="45%" stopColor={color} />
                                </linearGradient>
                                <linearGradient id={tailGradientId(bubbleIdPrefix, key)} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={mixHex(soft, '#ffffff', TAIL_SHEEN_AMOUNT)} />
                                    <stop offset="45%" stopColor={soft} />
                                </linearGradient>
                            </React.Fragment>
                        );
                    })}
                </defs>
                <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
                    {tailDots.map((d) => (
                        <circle
                            key={d.id}
                            data-testid="clusters-tail-dot"
                            data-id={d.id}
                            cx={d.x}
                            cy={d.y}
                            r={d.r}
                            fill={`url(#${tailGradientId(bubbleIdPrefix, d.catKey)})`}
                            opacity={dimFor(d.id, d.catKey)}
                            style={{cursor: 'default', transition: 'opacity 350ms'}}
                            onMouseMove={(e) => handleBubbleMove(e, d.data)}
                            onMouseLeave={handleBubbleLeave}
                        />
                    ))}
                    {simNodes.map((n) => {
                        const dim = dimFor(n.id, n.catKey);
                        const isSelected = expandedId === n.id;
                        return (
                            <g
                                key={n.id}
                                data-testid="clusters-bubble"
                                data-id={n.id}
                                className={dim < 1 ? 'clusters-bubble-dim' : undefined}
                                transform={`translate(${n.x || 0},${n.y || 0})`}
                                style={{cursor: 'pointer', opacity: dim, transition: 'opacity 350ms'}}
                                onClick={() => handleSelect(n.id)}
                                onMouseMove={(e) => handleBubbleMove(e, n.data)}
                                onMouseLeave={handleBubbleLeave}
                            >
                                <circle
                                    r={n.r}
                                    fill={`url(#${categoryGradientId(bubbleIdPrefix, n.catKey)})`}
                                    filter={bubbleShadow(bubbleIdPrefix)}
                                />
                                {isSelected && <BubbleSelectionRing r={n.r} stroke={palette.chrome.ink} />}
                            </g>
                        );
                    })}
                </g>
                {/*
                 * Labels live outside the zoomed group: each is re-fitted to its
                 * bubble's on-screen radius on every zoom change, so text stays a
                 * readable size and more of the headline appears as you zoom in.
                 */}
                <g data-testid="clusters-label-layer" style={{pointerEvents: 'none'}}>
                    {categoryMarks.map((m) => (
                        <text
                            key={`cat-${m.key}`}
                            data-testid="clusters-category-label"
                            x={transform.x + m.x * transform.k}
                            y={transform.y + m.top * transform.k}
                            textAnchor="middle"
                            fontFamily={FONT_STACK}
                            fontWeight={600}
                            fontSize={CATEGORY_LABEL_FONT_SIZE}
                            fill={palette.chrome.ink}
                            opacity={CATEGORY_LABEL_OPACITY}
                            style={{pointerEvents: 'none', letterSpacing: '0.04em', textTransform: 'uppercase'}}
                        >
                            {m.label}
                        </text>
                    ))}
                    {[...tailDots, ...simNodes].map((c) => {
                        const label = computeLabel(measure, c.data, c.r * transform.k);
                        if (!label) return null;
                        const x = transform.x + (c.x || 0) * transform.k;
                        const y = transform.y + (c.y || 0) * transform.k;
                        const total = label.lines.length * label.lineHeight + label.subLines.length * label.subLineHeight;
                        const y0 = y - total / 2 + label.fontSize * 0.85;
                        const subY0 = y0 + (label.lines.length - 1) * label.lineHeight;
                        return (
                            <g
                                key={c.id}
                                data-testid="clusters-label"
                                data-id={c.id}
                                style={{opacity: dimFor(c.id, c.catKey), transition: 'opacity 350ms'}}
                            >
                                <text
                                    x={x}
                                    y={y0}
                                    textAnchor="middle"
                                    fontFamily={FONT_STACK}
                                    fontWeight={600}
                                    fontSize={label.fontSize}
                                    fill="#fff"
                                    stroke="rgba(0,0,0,.25)"
                                    strokeWidth={2}
                                    style={{paintOrder: 'stroke'}}
                                >
                                    {label.lines.map((l, i) => (
                                        <tspan key={i} x={x} dy={i ? label.lineHeight : 0}>
                                            {l}
                                        </tspan>
                                    ))}
                                </text>
                                {label.subLines.map((l, i) => (
                                    <text
                                        key={i}
                                        x={x}
                                        y={subY0 + label.subLineHeight * (i + 1)}
                                        textAnchor="middle"
                                        fontFamily={FONT_STACK}
                                        fontWeight={500}
                                        fontSize={label.subFontSize}
                                        fill="rgba(255,255,255,.85)"
                                    >
                                        {l}
                                    </text>
                                ))}
                            </g>
                        );
                    })}
                </g>
            </svg>

            {categories.length > 0 && (
                <div
                    data-testid="clusters-legend"
                    className="absolute left-3 bottom-3 z-10 grid rounded-[10px] border border-line overflow-y-auto"
                    style={{
                        maxHeight: LEGEND_MAX_HEIGHT,
                        gap: LEGEND_ROW_GAP,
                        padding: `${LEGEND_PAD_Y}px 12px`,
                        fontSize: 12,
                        background: withAlpha(palette.chrome.surface, LEGEND_BG_ALPHA),
                    }}
                >
                    {categories.map((entry) => {
                        const hot = hoverCat === entry.key;
                        return (
                            <div
                                key={entry.key}
                                data-testid="clusters-legend-row"
                                data-cat={entry.key}
                                className="flex items-center gap-2 rounded-md"
                                style={{
                                    height: LEGEND_ROW_HEIGHT,
                                    padding: '0 6px',
                                    margin: '0 -6px',
                                    color: palette.chrome.ink,
                                    background: hot ? palette.chrome.gridline : 'transparent',
                                }}
                                onMouseEnter={() => setHoverCat(entry.key)}
                                onMouseLeave={() => setHoverCat((prev) => (prev === entry.key ? null : prev))}
                            >
                                <span
                                    className="shrink-0 rounded-full"
                                    style={{width: 12, height: 12, background: entry.color}}
                                />
                                <span className="whitespace-nowrap">{entry.label}</span>
                                <span
                                    className="ml-auto pl-3 whitespace-nowrap"
                                    style={{color: palette.chrome.inkMuted, fontVariantNumeric: 'tabular-nums'}}
                                >
                                    {entry.total}
                                </span>
                            </div>
                        );
                    })}
                    <div style={{color: palette.chrome.inkMuted, fontSize: 11}}>
                        stories on the map · hover a category
                    </div>
                </div>
            )}

            <div
                data-testid="clusters-layout-toggle"
                role="group"
                aria-label="Map layout"
                className="absolute right-3 top-3 z-30 flex rounded-xl border border-line bg-surface shadow-sm overflow-hidden text-xs"
            >
                {(
                    [
                        ['topic', 'By topic'],
                        ['size', 'By size'],
                    ] as const
                ).map(([mode, text]) => {
                    const pressed = activeLayout === mode;
                    const disabled = mode === 'topic' && !topicAvailable;
                    return (
                        <button
                            key={mode}
                            type="button"
                            data-testid={`clusters-layout-${mode}`}
                            aria-pressed={pressed}
                            disabled={disabled}
                            title={disabled ? TOPIC_UNAVAILABLE_HINT : undefined}
                            className={`px-3 py-1.5 ${mode === 'size' ? 'border-l border-line ' : ''}${
                                pressed ? 'bg-ghost-bg text-ink font-semibold' : 'text-ink-muted hover:bg-ghost-bg'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                            onClick={() => handleLayoutChange(mode)}
                        >
                            {text}
                        </button>
                    );
                })}
            </div>

            <div
                data-testid="clusters-zoom-controls"
                className="absolute right-3 bottom-3 z-10 flex flex-col rounded-xl border border-line bg-surface shadow-sm overflow-hidden"
            >
                <button
                    type="button"
                    data-testid="clusters-zoom-in"
                    aria-label="Zoom in"
                    className="px-2.5 py-1.5 text-sm text-ink-muted hover:bg-ghost-bg"
                    onClick={() => handleZoomBy(1.3)}
                >
                    +
                </button>
                <button
                    type="button"
                    data-testid="clusters-zoom-out"
                    aria-label="Zoom out"
                    className="px-2.5 py-1.5 text-sm text-ink-muted hover:bg-ghost-bg border-t border-line"
                    onClick={() => handleZoomBy(1 / 1.3)}
                >
                    −
                </button>
                <button
                    type="button"
                    data-testid="clusters-zoom-reset"
                    aria-label="Reset zoom"
                    className="px-2.5 py-1 text-[10px] uppercase tracking-wide text-ink-muted hover:bg-ghost-bg border-t border-line"
                    onClick={handleResetView}
                >
                    Reset
                </button>
            </div>

            {/* Suppressed while a detail card is open — the card already shows the headline. */}
            {tip && tooltipPos && !expandedNode && (
                <div
                    ref={tipRef}
                    data-testid="clusters-bubble-tooltip"
                    style={{
                        position: 'absolute',
                        left: tooltipPos.left,
                        top: tooltipPos.top,
                        maxWidth: TOOLTIP_MAX_WIDTH,
                        pointerEvents: 'none',
                        background: '#1b1b22',
                        color: '#f2f2f7',
                        borderRadius: 10,
                        padding: '8px 11px',
                        boxShadow: '0 10px 28px rgba(0,0,0,.30)',
                        fontSize: 12,
                        lineHeight: 1.35,
                        opacity: tipEntered ? 1 : 0,
                        transition: 'opacity 120ms ease',
                        zIndex: 20,
                    }}
                >
                    <span style={{display: 'block', fontWeight: 700, marginBottom: 3}}>
                        {headlineFor(tip.story)}
                    </span>
                    <span style={{display: 'block', color: '#a9a9bd', fontSize: 11}}>
                        {subLabelFor(tip.story)}
                    </span>
                </div>
            )}

            {expandedNode &&
                (() => {
                    const cx = transform.x + expandedNode.x * transform.k;
                    const cy = transform.y + expandedNode.y * transform.k;
                    const r = expandedNode.r * transform.k;
                    const panelWidth = 300;
                    const overflowsRight = cx + r + 16 + panelWidth > size.w;
                    const left = overflowsRight
                        ? Math.max(8, cx - r - 16 - panelWidth)
                        : Math.min(size.w - panelWidth - 8, cx + r + 16);
                    const top = Math.max(8, Math.min(size.h - 320, cy - 100));
                    return (
                        <div
                            data-testid="clusters-detail-overlay"
                            className="absolute z-20"
                            style={{left, top, width: panelWidth}}
                        >
                            <TopicDetailPanel
                                loading={detailLoading}
                                error={detailError}
                                detail={detail}
                                onClose={() => setExpandedId(null)}
                            />
                        </div>
                    );
                })()}
        </div>
    );
};

export default ClustersGraph;
